import { EditModes, TemplateTypes, GridControlTypes, NotificationTypes, GridTypes, TextBlockCategory } from "Enums";
import { IForm, IUserGridConfigDefinition } from "Grids/Interfaces";
import { IControl } from "Controls/Interfaces";
import { Navigation, ApplicationParameter, Util } from "Core";
import { IFormDefinition, IFormColumnDefinition } from "Grids/Interfaces";
import { GridSettings } from "Configuration";
import { IDataRow } from "Entities";
import { TextBlockService, CommandService, ComboboxService, GridDefinitionService, DatabaseImageService } from "Service";
import { ControlEventTypes, GridEventTypes } from "Core/Events/EventTypes";
import { DataConstants } from "Constants";
import { EventArgs } from "Core/Events";
import { BaseForm } from "./BaseForm";

/**
 * ## Form
 *
 * Edit data in a form.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Form with the name GridName
 * // In script:
 * @@grd:GridName@@
 *
 * // On page:
 * $dms("GridName")
 *
 * // First Form on page:
 * $dms(".dms-form")
 * ```
 */
export class Form extends BaseForm implements IForm {
    /**
     * @hidden
     */
    protected formDefinition: IFormDefinition;

    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/Form.html");

    /**
     * @hidden
     */
    public static formContentTemplate: string = require("./Templates/FormContent.html");

    /**
     * @hidden
     */
    public static tabTemplate: string = require("./Templates/Tab.html");

    /**
     * @hidden
     */
    public static groupTemplate: string = require("./Templates/Group.html");

    /**
     * @hidden
     */
    public static columnsTemplate: string = require("./Templates/FormColumns.html");

    /**
     * @hidden
     */
    public static columnTemplate: string = require("./Templates/FormColumn.html");

    /**
     * Tabstrip if tabs exist.
     * @hidden
     */
    private tabStrip: kendo.ui.TabStrip;

    /**
     * Selected tab in TabStrip.
     * @hidden
     */
    private selectedTabName: string;

    /**
     * Create a new Form.
     * @param formDefinition FormDefinition.
     * @param gridSettings GridSettings.
     * @hidden
     */
    constructor(
        formDefinition: IFormDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        comboboxService: ComboboxService,
        commandService: CommandService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService
    ) {
        super(formDefinition, textBlockCategory, gridSettings, comboboxService, commandService, gridDefinitionService, textBlockService, databaseImageService);
        this.formDefinition = formDefinition;
        this.isAutoResize = Util.stringIsNullOrEmpty(formDefinition.GridHeight);
    }

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    protected async setElementInternal(element: JQuery): Promise<void> {
        if (this.getGridType() == GridTypes.Form) {
            this.jQueryElement.css("min-height", "400px");
        }
        await this.textBlockService.getAll();
        let qryEditMode = ApplicationParameter.get("editMode");

        if (qryEditMode == "add") {
            this.editMode = EditModes.Add;
            this.fields[DataConstants.ROW_INDEX_COLUMN_NAME] = -1;
        }

        // Initialize controls
        this.fields = this.initializeRows(this.formDefinition.Columns, this.fields);
        for (let i = 0; i < this.formDefinition.Groups.length; i++) {
            this.fields = this.initializeRows(this.formDefinition.Groups[i].Columns, this.fields);
        }
        for (let i = 0; i < this.formDefinition.Tabs.length; i++) {
            for (let k = 0; k < this.formDefinition.Tabs[i].Groups.length; k++) {
                this.fields = this.initializeRows(this.formDefinition.Tabs[i].Groups[k].Columns, this.fields);
            }
            this.fields = this.initializeRows(this.formDefinition.Tabs[i].Columns, this.fields);
        }
        this.fields = this.initializeColumns(this.formDefinition.Buttons, this.fields);
        this.fields = this.initializeColumns(this.formDefinition.Submitbuttons, this.fields);
    }

    /**
     * Initialize the form.
     * @hidden
     */
    protected initialize(): void {
        if (this.currentUserGridConfig) {
            try {
                let config = this.currentUserGridConfig.Configuration;
                let storedViewModel = JSON.parse(config);
                $.extend(this._viewModel, storedViewModel);
            } catch (e) {
                console.log("error in usergridconfig");
            }
        }

        this.initializeControlTemplates(<IDataRow>(<any>this._viewModel));
        this.disposeEditContext();
        this.editContext.initialize(this.editMode);

        this.jQueryElement.empty();
        this.setBusy(true);
        let formContainer = "";
        let template = kendo.template(this.gridSettings.getTemplate(TemplateTypes.Content), { useWithBlock: false });
        formContainer = template(this);

        this.jQueryElement.append(formContainer);
        this.editContext.setDataRowFunction(() => this.getData());
        this.initializeJQueryControls();

        kendo.bind(this.jQueryElement, this._viewModel);

        this.tabStrip = this.jQueryElement.find(".dms-tab:first").data("kendoTabStrip");

        if (this.selectedTabName) {
            this.tabStrip.select(`[data-dms-tabname='${this.selectedTabName}']`);
        }

        this.initializeTooltips();

        this.editContext.setOnAfterValidateFunction((isValid, firstInvalidControl) => this.onAfterValidate(isValid, firstInvalidControl));
        this.editContext.load(this.textBlockCategory, this.jQueryElement, this._viewModel).then(() => {
            this.trigger(GridEventTypes.ControlDataLoaded);
        });
        this.jQueryElement.css("min-height", "");
        if (this.height != null) {
            this.setHeight(this.height);
        }
        this.trigger(GridEventTypes.ResizeRequested);

        if (this.tabStrip) {
            this.tabStrip.select();
        }
        setTimeout(() => this.setBusy(false));
    }

    private onAfterValidate(isValid: boolean, firstInvalidControl: IControl) {
        if (!isValid && firstInvalidControl) {
            const tabGroup = this.getTabGroupForControl(firstInvalidControl.getControlId());
            if (tabGroup) {
                if (tabGroup.tabName) {
                    this.tabStrip.select(`[data-dms-tabname='${tabGroup.tabName}']`);
                }
                if (tabGroup.groupId) {
                    const panelBar = this.jQueryElement.find(`[data-dms-groupid='${tabGroup.groupId}']`).data("kendoPanelBar");
                    if (panelBar) {
                        panelBar.expand(panelBar.element.find("li:first"), false);
                    }
                }
            }
            firstInvalidControl.getOuterElement()[0].scrollIntoView();
            firstInvalidControl.focus();
        }
    }

    /**
     * Register additional control events.
     * @param ctl Control.
     * @hidden
     */
    protected registerControlEvents(ctl: IControl): void {
        ctl.on(ControlEventTypes.Resolve, (e) => this.onResolveScript(e.sender, <string>e.data));

        switch (ctl.getControlType()) {
            // update/save button
            case GridControlTypes.Update:
                ctl.on(ControlEventTypes.BeforeCommand, () => this.setBusy(true));
                ctl.on(ControlEventTypes.AfterCommand, () => this.afterUpdate());
                ctl.on(ControlEventTypes.AfterCommandCancel, () => this.afterCancel());
                break;

            // add button
            case GridControlTypes.Add:
                ctl.on(ControlEventTypes.BeforeCommand, () => this.setBusy(true));
                ctl.on(ControlEventTypes.AfterCommand, () => this.afterAdd());
                ctl.on(ControlEventTypes.AfterCommandCancel, () => this.afterCancel());
                break;
        }
    }

    /**
     * Invoked after a command has been cancelled.
     * @hidden
     */
    protected afterCancel() {
        this.setBusy(false);
    }

    /**
     * Invoked after the add button command was finished.
     * @hidden
     */
    private afterAdd(): void {
        this.reload();
        this.notifyIfEnabled(this.textBlockService.get("NotificationAddRowSuccess", TextBlockCategory.global), NotificationTypes.Success);
    }

    /**
     * Invoked after the update button command was finished.
     * @hidden
     */
    private afterUpdate(): void {
        this.reload();
        this.notifyIfEnabled(this.textBlockService.get("NotificationEditRowSuccess", TextBlockCategory.global), NotificationTypes.Success);
    }

    private getTabGroupForControl(controlId: number): { tabName: string; groupId: number } {
        if (this.formDefinition.Tabs) {
            for (const tab of this.formDefinition.Tabs) {
                for (const group of tab.Groups) {
                    if (this.isControlInColumn(controlId, group.Columns)) {
                        return { tabName: tab.TabName, groupId: group.GroupID };
                    }
                }
                if (this.isControlInColumn(controlId, tab.Columns)) {
                    return { tabName: tab.TabName, groupId: null };
                }
            }

            for (const group of this.formDefinition.Groups) {
                if (this.isControlInColumn(controlId, group.Columns)) {
                    return { tabName: null, groupId: group.GroupID };
                }
            }
        }
        return null;
    }

    private isControlInColumn(controlId: number, columns: IFormColumnDefinition[][]): boolean {
        if (columns) {
            for (const column of columns) {
                if (column) {
                    for (const row of column) {
                        if (row) {
                            for (const control of row.Controls) {
                                if (control.ControlID == controlId) {
                                    return true;
                                }
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    /**
     * Initialize rows for this form.
     * @param columns List of columns.
     * @param fields Fields which will be filled.
     * @hidden
     */
    protected initializeRows(columns: IFormColumnDefinition[][], fields: {}): {} {
        if (columns) {
            for (let row = 0, len = columns.length; row < len; row++) {
                fields = $.extend(fields, this.initializeColumns(columns[row], fields));
            }
        }
        return fields;
    }

    /**
     * Initialize all controls rendered in the template.
     * @hidden
     */
    protected initializeJQueryControls(): void {
        this.initializeControlsInContainer(this.jQueryElement, this.dmsControlDictionary);
    }

    /**
     * Loads data if the {Form} is in edit-mode.
     * @hidden
     */
    protected async loadData(): Promise<void> {
        if (this.tabStrip) {
            let tab = this.tabStrip.select();
            if (tab) {
                this.selectedTabName = tab.attr("data-dms-tabname");
            }
        }

        // Initialize view model
        this._viewModel = new kendo.data.Model(this.fields);

        if (this.editMode == EditModes.Edit) {
            setTimeout(() => this.setBusy(true));
            try {
                let data = await this.commandService.getFormData(this.gridSettings.dataUrlGet, this.formDefinition.GridID);
                $.extend(this._viewModel, data); // merge the default values with the data received by service.
                this.storeOriginalDataRow([this._viewModel]);
                this.initialize();
                this.trigger(GridEventTypes.DataBound, new EventArgs(this, null));
            } catch (error) {
                Navigation.errorAsync(error, "Error");
                this.setBusy(false);
            }
        } else {
            this.initialize();
            this.trigger(GridEventTypes.DataBound, new EventArgs(this, null));
        }
    }

    /**
     * Reloads data of this grid.
     * @hidden
     */
    protected reloadInternal(): Promise<void> {
        this.show();
        return this.loadData();
    }

    /**
     * Set the height of the grid.
     * @param height Height in pixel.
     * @since 4.0.0
     */
    public setHeight(height: string | number): void {
        super.setHeight(height);
        this.jQueryElement.outerHeight(height);
        let footerHeight = Math.floor(this.jQueryElement.find("> div > .dms-form-footer").outerHeight(true)) - 1;
        let formHeight = this.jQueryElement.outerHeight(true) - footerHeight;
        if (this.tabStrip) {
            let tabElement = this.tabStrip.element;
            let tabHeaderHeight = tabElement.find("> .k-tabstrip-items").outerHeight(true);
            let tabPosition = tabElement.position().top;
            tabElement.find("> .dms-tab-item").outerHeight(formHeight - tabPosition - tabHeaderHeight);
        }
        this.jQueryElement.find("> div > .dms-form-content").outerHeight(formHeight);

        this.trigger(GridEventTypes.HeightChanged, { height: height });
    }

    /**
     * Get the current state of the grid as user grid config.
     * @hidden
     */
    public createUserGridConfig(): string {
        let viewModel = $.extend({}, this._viewModel);
        delete viewModel[DataConstants.ROW_INDEX_COLUMN_NAME];
        return JSON.stringify(viewModel);
    }

    /**
     * Apply the given user grid config to the grid.
     * @param config Selected configuration.
     * @hidden
     */
    public async applyUserGridConfig(config: IUserGridConfigDefinition): Promise<void> {
        await super.applyUserGridConfig(config);
        this.dispose();
        this.jQueryElement.empty();
    }

    /**
     * Dispose the grid and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        this.disposeEditContext();
    }
}
